import React, { Dispatch, SetStateAction, useCallback } from 'react'

import TagMenu from 'components/Shared/TagMenu'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { closeTagMenu, handleAddNewTag } from '../Helpers/Helper'
import { updateContributionTags } from 'redux/thunks/TextAnalysis'
import { resetAllTags } from 'redux/slices/TextAnalysis'
import { handleDeleteTag, onAddNewTagHandler } from '../Helpers/TagHelper'
import { SingleTagProps, Tag } from '../Types'
import { TAResponseProps } from 'types/TextAnalysis'
import { useAlert } from 'components/Shared/Alert'

interface TagMenuContainerProps {
  item: TAResponseProps
  index: number
  anchorElMap: { [key: number]: HTMLElement | null }
  setAnchorElMap: Dispatch<
    SetStateAction<{ [key: number]: HTMLElement | null }>
  >
  cardActionsRefs: HTMLDivElement | null
}

const TagMenuContainer = ({
  item,
  index,
  anchorElMap,
  setAnchorElMap,
  cardActionsRefs,
}: TagMenuContainerProps) => {
  const dispatch = useAppDispatch()
  const { showAlert } = useAlert()
  const {
    tags: { tagsList, fetchingTags },
  } = useAppSelector((state) => state.textAnalysis)

  const onCloseTagMenu = useCallback(
    (selectedTags, tagsChangedStatus) => {
      if (tagsChangedStatus) {
        const payload = handleAddNewTag(item, selectedTags)
        dispatch(updateContributionTags(payload))
      }
      dispatch(resetAllTags())
      closeTagMenu(index, setAnchorElMap)
    },
    [dispatch, index, setAnchorElMap, item],
  )

  const onAddNewTag = useCallback(
    async (type: string, tagToAdd: (Tag | string)[]) => {
      await onAddNewTagHandler(dispatch, item, index, tagToAdd, type, showAlert)
    },
    [dispatch, index, item],
  )

  const onTagDelete = useCallback(
    (tagToRemove: SingleTagProps) => {
      handleDeleteTag(dispatch, item, index, tagToRemove, 'tagmenuTags')
    },
    [dispatch, index, item],
  )

  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent,
      selectedTags: string[],
      tagsChangedStatus: boolean,
    ) => {
      const eventType = e.key === 'Escape' || e.key === 'Tab'
      if (eventType && cardActionsRefs) {
        e.preventDefault()
        onCloseTagMenu(selectedTags, tagsChangedStatus)
        cardActionsRefs.focus()
      }
    },
    [onCloseTagMenu, cardActionsRefs],
  )

  if (!anchorElMap[index]) {
    return null
  }

  return (
    <TagMenu
      id={`tag-menu-${index + 1}`}
      fetching={fetchingTags}
      tags={tagsList}
      tagLabel="name"
      label="Add Tag"
      placeholder="Search or create new tag"
      showHelpText
      contributionTags={item?.tags || []}
      open={Boolean(anchorElMap[index])}
      anchorEl={anchorElMap[index]}
      onCloseTagMenu={onCloseTagMenu}
      onAddTagToContribution={onAddNewTag}
      onTagDelete={onTagDelete}
      handleKeyDown={handleKeyDown}
    />
  )
}

export default React.memo(TagMenuContainer)
